import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import React, { useState } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { FiPlus, FiTrash2, FiClock } from 'react-icons/fi';

// Step Item Component
const StepItemComponent = ({ item, index, onUpdate, onDelete, isEditing }) => {
  const handleTitleChange = (e) => {
    onUpdate(index, { ...item, title: e.target.value });
  };

  const handleDescriptionChange = (e) => {
    onUpdate(index, { ...item, description: e.target.value });
  };

  return (
    <div className="step-item">
      <div className="step-number">{index + 1}</div>
      <div className="step-content">
        {isEditing ? (
          <>
            <input
              type="text"
              value={item.title}
              onChange={handleTitleChange}
              placeholder="Step title..."
              className="step-title-input"
            />
            <textarea
              value={item.description}
              onChange={handleDescriptionChange}
              placeholder="Step description..."
              className="step-description-input"
              rows={2}
            />
            <button
              type="button"
              onClick={() => onDelete(index)}
              className="step-delete-btn"
            >
              <FiTrash2 size={14} />
              Remove
            </button>
          </>
        ) : (
          <>
            <div className="step-title">{item.title || 'Step Title'}</div>
            <div className="step-description">
              {item.description || 'Step description goes here...'}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Steps Node View Component
const StepsNodeView = ({ node, updateAttributes, selected }) => {
  const [isEditing, setIsEditing] = useState(true);
  const { title, items } = node.attrs;

  const handleTitleChange = (e) => {
    updateAttributes({ title: e.target.value });
  };

  const handleUpdateItem = (index, newItem) => {
    const newItems = [...items];
    newItems[index] = newItem;
    updateAttributes({ items: newItems });
  };

  const handleDeleteItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    updateAttributes({ items: newItems });
  };

  const handleAddItem = () => {
    updateAttributes({
      items: [...items, { title: '', description: '' }],
    });
  };

  return (
    <NodeViewWrapper className={`steps-block ${selected ? 'selected' : ''}`}>
      <div className="steps-header">
        <FiClock size={16} />
        <span>Step by Step</span>
        <button
          type="button"
          onClick={() => setIsEditing(!isEditing)}
          className="steps-edit-toggle"
        >
          {isEditing ? 'Preview' : 'Edit'}
        </button>
      </div>
      {isEditing ? (
        <input
          type="text"
          value={title}
          onChange={handleTitleChange}
          placeholder="Enter section title..."
          className="steps-title-input"
        />
      ) : (
        title && <div className="steps-block-title">{title}</div>
      )}
      <div className="steps-items">
        {items.map((item, index) => (
          <StepItemComponent
            key={index}
            item={item}
            index={index}
            onUpdate={handleUpdateItem}
            onDelete={handleDeleteItem}
            isEditing={isEditing}
          />
        ))}
      </div>
      {isEditing && (
        <button type="button" onClick={handleAddItem} className="steps-add-btn">
          <FiPlus size={16} />
          <span>Add Step</span>
        </button>
      )}
    </NodeViewWrapper>
  );
};

// Steps Extension
export const Steps = Node.create({
  name: 'steps',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      title: {
        default: 'How to Get Started',
      },
      items: {
        default: [
          { title: 'Step 1', description: 'Description for step 1' },
          { title: 'Step 2', description: 'Description for step 2' },
          { title: 'Step 3', description: 'Description for step 3' },
        ],
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="steps"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'steps' }), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(StepsNodeView);
  },

  addCommands() {
    return {
      insertSteps:
        (attrs = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              title: attrs.title || 'How to Get Started',
              items: attrs.items || [
                { title: 'Step 1', description: 'Description for step 1' },
                { title: 'Step 2', description: 'Description for step 2' },
                { title: 'Step 3', description: 'Description for step 3' },
              ],
            },
          });
        },
    };
  },
});

export default Steps;
